import { t } from 'i18next'
import Joi from 'joi'
import _ from 'lodash'

import { regexToAllowedChars } from '@hapana/shared/utils/string.util'
import {
  ABOUT_MAX_LENGTH,
  INSTAGRAM_HANDLE_MAX_LENGTH,
  PATTERNS,
} from '@hapana/shared/utils/validations.util'
import { StaffAccessRuleAssignmentType } from '../../../../../../web/services/gqty'
import { weekdaysTimeStartToEnd } from '@hapana/shared/utils/date.util'

export const SITE_IDS_KEY = 'siteIds'
export const SITE_GROUP_IDS_KEY = 'siteGroupIds'
const PROFILE_IMAGE_PATH = '/profileImage/thumbnail/'

export const POLICY_TYPE = {
  BY_SITE: 'BY_SITE',
  BY_SITE_GROUP: 'SITE_GROUPS',
}

export const LINK_POLICY_RADIO_OPTIONS = [
  { type: StaffAccessRuleAssignmentType.DIRECT_SITES, label: 'Locations' },
  { type: StaffAccessRuleAssignmentType.SITE_GROUPS, label: 'Location groups' },
]

export const FIELD_NAMES = {
  FIRST_NAME: 'firstName',
  LAST_NAME: 'lastName',
  PHONE: 'phone',
  EMAIL: 'email',
  EMPLOYEE_NUMBER: 'employeeNumber',
  IMAGE: 'image',
  ABOUT: 'about',
  HOBBIES: 'hobbies',
  INSTAGRAM: 'instagramHandle',
  USER_ACCESS_RULES: 'accessRules',
  POLICY: 'policy',
  SITES: 'sites',
  SITE_GROUPS: 'siteGroups',
  RULE_ID: 'ruleId',
  ENABLE_WORKING_HOURS: 'enableWorkingHours',
  SINGLE_HOUR: 'singleHour',
  HAS_CUSTOM_HOURS: 'hasCustomHours',
  CUSTOM_HOURS: 'customHours',
  IS_INSTRUCTOR: 'isInstructor',
}

export const PROFILE_FIELDS = [
  FIELD_NAMES.FIRST_NAME,
  FIELD_NAMES.LAST_NAME,
  FIELD_NAMES.EMAIL,
  FIELD_NAMES.PHONE,
  FIELD_NAMES.ABOUT,
  FIELD_NAMES.HOBBIES,
  FIELD_NAMES.INSTAGRAM,
]

export const INITIAL_USER_ACCESS_RULES = {
  [FIELD_NAMES.POLICY]: {
    id: null,
    name: '',
    type: POLICY_TYPE.BY_SITE,
  },
  [FIELD_NAMES.SITES]: {
    list: [],
    siteIds: [],
  },
  [FIELD_NAMES.SITE_GROUPS]: {
    list: [],
    siteGroupIds: [],
  },
}

export const ADD_EDIT_USER_FORM_INITIAL_VALUES = {
  [FIELD_NAMES.FIRST_NAME]: '',
  [FIELD_NAMES.LAST_NAME]: '',
  [FIELD_NAMES.PHONE]: '',
  [FIELD_NAMES.EMAIL]: '',
  [FIELD_NAMES.EMPLOYEE_NUMBER]: '',
  [FIELD_NAMES.ABOUT]: '',
  [FIELD_NAMES.HOBBIES]: '',
  [FIELD_NAMES.INSTAGRAM]: '',
  [FIELD_NAMES.USER_ACCESS_RULES]: [],
  [FIELD_NAMES.ENABLE_WORKING_HOURS]: false,
  [FIELD_NAMES.HAS_CUSTOM_HOURS]: false,
  [FIELD_NAMES.CUSTOM_HOURS]: {},
  [FIELD_NAMES.SINGLE_HOUR]: {
    startTime: null,
    endTime: null,
  },
  [FIELD_NAMES.IS_INSTRUCTOR]: false,
}

export const USER_ACCESS_TEST_IDS = {
  LINK_POLICY_LOCATION: 'link-policy-location',
  LINK_POLICY_LOCATION_GROUP: 'link-policy-location-group',
  LINK_POLICY_ACCESS_POLICY: 'link-policy-access-policy',
}

export const BASE_DROPDOWN_MENU_PROPS = {
  PaperProps: {
    style: {
      marginTop: 8,
    },
  },
  anchorOrigin: {
    vertical: 'bottom',
    horizontal: 'left',
  },
  transformOrigin: {
    vertical: 'top',
    horizontal: 'left',
  },
}

const RULES = {
  NAME_MAX_LENGTH: 65,
}

export const ACCESS_OPTIONS = {
  brand: {
    label: t('common:userAccess.tabs.users.table.brand'),
    key: 'corporate',
  },
  location: {
    label: t('common:userAccess.tabs.users.table.location'),
    key: 'access',
  },
  noAccess: {
    label: t('common:userAccess.tabs.users.table.noAccess'),
    key: 'NO_ACCESS',
  },
}

export const USERS_STATUSES_OPTIONS = {
  active: {
    key: 'active',
    label: t('common:userAccess.tabs.users.table.statuses.active'),
  },
  inactive: {
    key: 'inactive',
    label: t('common:userAccess.tabs.users.table.statuses.inactive'),
  },
  allUsers: {
    key: 'ALL',
    label: t('common:userAccess.tabs.users.table.statuses.allUsers'),
  },
}

export const FILTER_OPERATORS = {
  EQUAL: 'EQUAL',
  NOT_EQUAL: 'NOT_EQUAL',
  GREATER_THAN: 'GREATER_THAN',
  GREATER_THAN_EQUAL: 'GREATER_THAN_EQUAL',
  LESS_THAN: 'LESS_THAN',
  LESS_THAN_EQUAL: 'LESS_THAN_EQUAL',
  IN: 'IN',
  NOT_IN: 'NOT_IN',
}

export const USER_ACCESS_TABLE_DATA_STRUCTURE = {
  id: {
    key: 'id',
  },
  profile: {
    label: t('common:userAccess.tabs.users.table.columns.nameAndEmail'),
    key: 'profile',
    serverSideKey: 'name',
  },
  accessPolicies: {
    label: t('common:userAccess.tabs.users.table.columns.accessPolicies'),
    key: 'accessPolicies',
    serverSideKey: 'accessPolicies',
    serverSideFilterOperator: FILTER_OPERATORS.IN,
  },
  locationGroup: {
    label: t('common:userAccess.tabs.users.table.columns.locationGroup'),
    key: 'siteGroups',
    serverSideKey: 'siteGroups',
    serverSideFilterOperator: FILTER_OPERATORS.IN,
  },
  location: {
    label: t('common:userAccess.tabs.users.table.columns.location'),
    key: 'sites',
    serverSideKey: 'sites',
    serverSideFilterOperator: FILTER_OPERATORS.IN,
  },
  levelAccess: {
    label: t('common:userAccess.tabs.users.table.columns.levelAccess'),
    key: 'accessLevel',
    serverSideKey: 'accessLevel',
    serverSideFilterOptions: [
      {
        label: ACCESS_OPTIONS.brand.label,
        value: ACCESS_OPTIONS.brand.key,
      },
      {
        label: ACCESS_OPTIONS.location.label,
        value: ACCESS_OPTIONS.location.key,
      },
      {
        label: ACCESS_OPTIONS.noAccess.label,
        value: ACCESS_OPTIONS.noAccess.key,
      },
    ],
  },
  status: {
    label: t('common:userAccess.tabs.users.table.columns.status'),
    key: 'status',
    serverSideKey: 'status',
    serverSideFilterOptions: [
      {
        label: USERS_STATUSES_OPTIONS.active.label,
        value: USERS_STATUSES_OPTIONS.active.key,
      },
      {
        label: USERS_STATUSES_OPTIONS.inactive.label,
        value: USERS_STATUSES_OPTIONS.inactive.key,
      },
    ],
  },
}

const ACCESS_LEVEL_MAPPING = {
  corporate: t('common:userAccess.tabs.users.table.brand'),
  access: t('common:userAccess.tabs.users.table.access'),
}

const INSTAGRAM_HANDLE_PATTERN = /^@?[a-zA-Z0-9._]{1,}$/

const NO_CURLY_BRACES = /^[^{}]*$/

export const ADD_EDIT_USER_SCHEMA = Joi.object({
  [FIELD_NAMES.FIRST_NAME]: Joi.string()
    .pattern(PATTERNS.ALPHANUMERIC_SPECIAL_WITH_WHITESPACE)
    .message(
      t('errors:forms.specialCharacters', {
        charList: regexToAllowedChars(PATTERNS.ALPHANUMERIC_SPECIAL_WITH_WHITESPACE).string,
        count: regexToAllowedChars(PATTERNS.ALPHANUMERIC_SPECIAL_WITH_WHITESPACE).length,
      })
    )
    .pattern(PATTERNS.NO_WHITESPACE_AT_START_END)
    .message(t('errors:forms.noWhitespace'))
    .pattern(NO_CURLY_BRACES)
    .message(t('errors:forms.pattern'))
    .max(RULES.NAME_MAX_LENGTH)
    .messages({
      'string.empty': t('errors:forms.required'),
      'string.max': t('errors:forms.maxLength', {
        count: RULES.NAME_MAX_LENGTH,
      }),
    }),
  [FIELD_NAMES.LAST_NAME]: Joi.string()
    .pattern(PATTERNS.ALPHANUMERIC_SPECIAL_WITH_WHITESPACE)
    .message(
      t('errors:forms.specialCharacters', {
        charList: regexToAllowedChars(PATTERNS.ALPHANUMERIC_SPECIAL_WITH_WHITESPACE).string,
        count: regexToAllowedChars(PATTERNS.ALPHANUMERIC_SPECIAL_WITH_WHITESPACE).length,
      })
    )
    .pattern(PATTERNS.NO_WHITESPACE_AT_START_END)
    .message(t('errors:forms.noWhitespace'))
    .pattern(NO_CURLY_BRACES)
    .message(t('errors:forms.pattern'))
    .max(RULES.NAME_MAX_LENGTH)
    .messages({
      'string.empty': t('errors:forms.required'),
      'string.max': t('errors:forms.maxLength', {
        count: RULES.NAME_MAX_LENGTH,
      }),
    }),
  [FIELD_NAMES.PHONE]: Joi.string()
    .allow('')
    .pattern(PATTERNS.PHONE_NUMBER)
    .messages({
      'string.pattern.base': t('errors:forms.phone'),
    }),
  [FIELD_NAMES.EMAIL]: Joi.string()
    .pattern(PATTERNS.EMAIL)
    .max(RULES.NAME_MAX_LENGTH)
    .messages({
      'string.pattern.base': t('errors:forms.invalidEmail'),
      'string.empty': t('errors:forms.required'),
    }),
  [FIELD_NAMES.EMPLOYEE_NUMBER]: Joi.string()
    .allow('', null)
    .pattern(/^(\d+)?$/)
    .messages({
      'string.pattern.base': t('errors:forms.invalidNumber'),
    })
    .optional(),
  [FIELD_NAMES.ABOUT]: Joi.string()
    .allow('')
    .max(ABOUT_MAX_LENGTH)
    .pattern(PATTERNS.ABOUT)
    .messages({
      'string.max': t('errors:forms.maxLength', { count: ABOUT_MAX_LENGTH }),
      'string.pattern.base': t('errors:forms.specialCharacters', {
        charList: regexToAllowedChars(PATTERNS.ABOUT).string,
        count: regexToAllowedChars(PATTERNS.ABOUT).length,
      }),
    })
    .empty('')
    .default(null),
  [FIELD_NAMES.HOBBIES]: Joi.string()
    .allow('')
    .max(ABOUT_MAX_LENGTH)
    .pattern(PATTERNS.ABOUT)
    .messages({
      'string.max': t('errors:forms.maxLength', { count: ABOUT_MAX_LENGTH }),
      'string.pattern.base': t('errors:forms.specialCharacters', {
        charList: regexToAllowedChars(PATTERNS.ABOUT).string,
        count: regexToAllowedChars(PATTERNS.ABOUT).length,
      }),
    })
    .empty('')
    .default(null),
  [FIELD_NAMES.INSTAGRAM]: Joi.string()
    .allow('')
    .max(INSTAGRAM_HANDLE_MAX_LENGTH)
    .custom((value, helpers) => {
      if (!value) {
        return null
      }
      if (value === '@') {
        return value
      }
      if (!INSTAGRAM_HANDLE_PATTERN.test(value)) {
        return helpers.error('string.pattern.base')
      }
      return value.startsWith('@') ? value.slice(1) : value
    })
    .messages({
      'string.max': t('errors:forms.maxLength', {
        count: INSTAGRAM_HANDLE_MAX_LENGTH,
      }),
      'string.pattern.base': t('errors:forms.specialCharacters', {
        charList: regexToAllowedChars(INSTAGRAM_HANDLE_PATTERN).string,
        count: regexToAllowedChars(INSTAGRAM_HANDLE_PATTERN).length,
      }),
    })
    .empty('')
    .default(null),
  [FIELD_NAMES.ENABLE_WORKING_HOURS]: Joi.boolean().optional(),
  [FIELD_NAMES.SINGLE_HOUR]: Joi.object({
    startTime: Joi.any().optional(),
    endTime: Joi.any().optional(),
  })
    .custom((value, helpers) => {
      const { enableWorkingHours } = helpers.state.ancestors[0]

      // Only validate if working hours are enabled
      if (!enableWorkingHours) {
        return value
      }

      const { startTime, endTime } = value || {}
      const hasStartTime = startTime !== null && startTime !== undefined && startTime !== ''
      const hasEndTime = endTime !== null && endTime !== undefined && endTime !== ''

      // If one is provided, both must be provided
      if (hasStartTime && !hasEndTime) {
        return helpers.error('object.missingEndTime')
      }
      if (hasEndTime && !hasStartTime) {
        return helpers.error('object.missingStartTime')
      }

      return value
    })
    .messages({
      'object.missingStartTime': t('errors:forms.workingHoursRequired'),
      'object.missingEndTime': t('errors:forms.workingHoursRequired'),
    })
    .optional(),
  [FIELD_NAMES.CUSTOM_HOURS]: Joi.object()
    .pattern(
      Joi.string(),
      Joi.object({
        startTime: Joi.any().optional(),
        endTime: Joi.any().optional(),
      })
        .custom((value, helpers) => {
          const { enableWorkingHours } = helpers.state.ancestors[1]

          // Only validate if working hours are enabled
          if (!enableWorkingHours) {
            return value
          }

          const { startTime, endTime } = value || {}
          const hasStartTime = startTime !== null && startTime !== undefined && startTime !== ''
          const hasEndTime = endTime !== null && endTime !== undefined && endTime !== ''

          // If one is provided, both must be provided
          if (hasStartTime && !hasEndTime) {
            return helpers.error('object.missingEndTime')
          }
          if (hasEndTime && !hasStartTime) {
            return helpers.error('object.missingStartTime')
          }

          return value
        })
        .messages({
          'object.missingStartTime': t('errors:forms.workingHoursRequired'),
          'object.missingEndTime': t('errors:forms.workingHoursRequired'),
        })
        .optional()
    )
    .optional(),
  [FIELD_NAMES.HAS_CUSTOM_HOURS]: Joi.boolean().optional(),
  [FIELD_NAMES.IS_INSTRUCTOR]: Joi.boolean().optional(),
}).unknown(true)

export const getFormattedImageName = (url, imageExtension) => {
  if (!url) {
    return null
  }

  const parts = url.split(PROFILE_IMAGE_PATH)
  if (parts.length <= 1) {
    const lastSegment = url.split('/').pop()
    const truncatedName = lastSegment.substring(0, 10)
    return `${truncatedName}...${imageExtension}`
  }

  const fullImageName = parts[1]
  const lastDotIndex = fullImageName.lastIndexOf('.')
  const extension = fullImageName.substring(lastDotIndex)
  const truncatedName = fullImageName.substring(0, 10)

  return `${truncatedName}...${extension}`
}

export const formatPolicyCardDescription = (items, label, t) => {
  if (!items || items.length === 0) {
    return null
  }

  const itemNames = items.map((item) => item.name)
  const displayNames = itemNames.slice(0, 2).join(', ')
  const additionalCount = itemNames.length > 2 ? ` + ${itemNames.length - 2}` : ''

  return `${t(label)}: ${displayNames}${additionalCount}`
}

export const getPolicyCardDescription = (entity, isListFormat = false, t) => {
  const siteGroups = isListFormat ? entity.siteGroups?.list : entity.siteGroups
  const sites = isListFormat ? entity.sites?.list : entity.sites

  const descriptions = [
    formatPolicyCardDescription(
      siteGroups,
      'common:userAccess.modals.addEditUser.groups.accessPolicies.locationGroups',
      t
    ),
    formatPolicyCardDescription(
      sites,
      'common:userAccess.modals.addEditUser.groups.accessPolicies.locations',
      t
    ),
  ].filter(Boolean)

  return descriptions
}

export const formatAccessPolicies = (accessPolicies) => {
  if (!accessPolicies) {
    return ''
  }
  const policyNames = accessPolicies.map((policy) => policy.policy?.name).filter(Boolean)
  if (policyNames.length > 2) {
    return `${policyNames.slice(0, 2).join(', ')}, ${policyNames.length - 2}+`
  } else {
    return policyNames.join(', ')
  }
}

const formatAccessLevels = (levels) => {
  if (!levels || levels.length === 0) {
    return ACCESS_OPTIONS.noAccess.label
  }
  return levels
    .map((level) => ACCESS_LEVEL_MAPPING[level] || level)
    .join(', ')
    .toLowerCase()
}

export const transformUserDataToTableValues = (userData) => {
  return userData.map((result) => ({
    [USER_ACCESS_TABLE_DATA_STRUCTURE.id.key]: result.node?.id,
    [USER_ACCESS_TABLE_DATA_STRUCTURE.profile.key]: {
      name: `${result.node.firstName || ''} ${result.node.lastName || ''}`.trim(),
      firstName: result.node.firstName || '',
      lastName: result.node.lastName || '',
      email: result.node.email || '',
      phone: result.node.phone || '',
      profileImage: result.node.profileImage?.url || '',
      hobbies: result.node.hobbies || '',
      about: result.node.about || '',
      instagramHandle: result.node.instagramHandle || '',
    },
    [USER_ACCESS_TABLE_DATA_STRUCTURE.accessPolicies.key]: formatAccessPolicies(
      result.node.access.rules
    ),
    [USER_ACCESS_TABLE_DATA_STRUCTURE.location.key]: result.node.access.rules
      .flatMap((rule) => rule.sites.map((site) => site.name))
      .join(', '),
    [USER_ACCESS_TABLE_DATA_STRUCTURE.locationGroup.key]: result.node.access.rules
      .flatMap((rule) => rule.siteGroups.map((group) => group.name))
      .join(', '),
    [USER_ACCESS_TABLE_DATA_STRUCTURE.levelAccess.key]: formatAccessLevels(
      result.node.access.levels
    ),
    [USER_ACCESS_TABLE_DATA_STRUCTURE.status.key]: result.node.status,
    access: result.node.access,
    id: result.node.id,
  }))
}

export const transformAccessRule = (rule, editPolicyType) => {
  if (!rule) {
    return INITIAL_USER_ACCESS_RULES
  }
  const includeSites =
    editPolicyType === null || editPolicyType === StaffAccessRuleAssignmentType.DIRECT_SITES
  const includeSiteGroups =
    editPolicyType === null || editPolicyType === StaffAccessRuleAssignmentType.SITE_GROUPS

  const toArray = (value, listKey) => {
    if (!value) {
      return []
    }
    if (Array.isArray(value)) {
      return value
    }
    if (Array.isArray(value[listKey])) {
      return value[listKey]
    }
    return []
  }

  const normalizeEntities = (collection, idKey) =>
    toArray(collection, 'list')
      .map((entity) => ({
        id: entity[idKey] ?? entity.id,
        name: entity.name,
      }))
      .filter(({ id }) => id !== undefined && id !== null)

  const normalizedSites = normalizeEntities(rule.sites, 'siteId')
  const normalizedSiteGroups = normalizeEntities(rule.siteGroups, 'siteGroupId')

  const siteIds =
    Array.isArray(rule?.sites?.siteIds) && rule.sites.siteIds.length > 0
      ? rule.sites.siteIds
      : normalizedSites.map((site) => site.id).filter(Boolean)
  const siteGroupIds =
    Array.isArray(rule?.siteGroups?.siteGroupIds) && rule.siteGroups.siteGroupIds.length > 0
      ? rule.siteGroups.siteGroupIds
      : normalizedSiteGroups.map((group) => group.id).filter(Boolean)

  const assignmentType =
    rule.assignmentType ||
    rule?.policy?.type ||
    (includeSites
      ? StaffAccessRuleAssignmentType.DIRECT_SITES
      : StaffAccessRuleAssignmentType.SITE_GROUPS)

  return {
    [FIELD_NAMES.RULE_ID]: rule[FIELD_NAMES.RULE_ID] ?? rule.staffAccessRuleId ?? null,
    [FIELD_NAMES.POLICY]: {
      id: rule.policy?.staffAccessPolicyId ?? rule.policy?.id ?? rule.staffAccessPolicyId ?? null,
      name: rule.policy?.name ?? '',
      type: assignmentType,
    },
    [FIELD_NAMES.SITES]: includeSites
      ? {
          list: normalizedSites,
          siteIds,
        }
      : {
          list: [],
          siteIds: [],
        },
    [FIELD_NAMES.SITE_GROUPS]: includeSiteGroups
      ? {
          list: normalizedSiteGroups,
          siteGroupIds,
        }
      : {
          list: [],
          siteGroupIds: [],
        },
  }
}

export const transformTableRowToFormValues = (userData) => {
  const workingHours = userData?.siteData?.workingHours || {}
  const isInstructor = userData?.siteData?.isInstructor ?? false

  const { customHoursEnabled = false, ...dayHours } = workingHours

  if (!workingHours) {
    return {
      [FIELD_NAMES.FIRST_NAME]: userData.firstName,
      [FIELD_NAMES.LAST_NAME]: userData.lastName,
      [FIELD_NAMES.EMAIL]: userData.email,
      [FIELD_NAMES.EMPLOYEE_NUMBER]:
        userData.employeeNumber != null ? String(userData.employeeNumber) : '',
      [FIELD_NAMES.PHONE]: userData.phone,
      [FIELD_NAMES.ABOUT]: userData.about,
      [FIELD_NAMES.HOBBIES]: userData.hobbies,
      [FIELD_NAMES.INSTAGRAM]: userData.instagramHandle,
      [FIELD_NAMES.IMAGE]: userData?.profileImage,
      [FIELD_NAMES.USER_ACCESS_RULES]: userData.rules.map((rule) => ({
        [FIELD_NAMES.RULE_ID]: rule.staffAccessRuleId,
        [FIELD_NAMES.POLICY]: {
          id: rule.policy?.staffAccessPolicyId,
          name: rule.policy?.name,
          type: rule.assignmentType,
        },
        [FIELD_NAMES.SITES]: {
          list:
            rule.sites
              .filter((site) => site.siteId !== undefined)
              ?.map((site) => ({
                id: site.siteId,
                name: site.name,
              })) ?? [],
          siteIds:
            rule.sites?.filter((site) => site.siteId !== undefined).map((site) => site.siteId) ??
            [],
        },
        [FIELD_NAMES.SITE_GROUPS]: {
          list:
            rule.siteGroups
              .filter((group) => group.siteGroupId !== undefined)
              ?.map((group) => ({
                id: group.siteGroupId,
                name: group.name,
              })) ?? [],
          siteGroupIds:
            rule.siteGroups
              ?.filter((group) => group.siteGroupId !== undefined)
              .map((group) => group.siteGroupId)
              .filter((id) => id !== undefined) ?? [],
        },
      })),
      [FIELD_NAMES.HAS_CUSTOM_HOURS]: workingHours.customHoursEnabled ?? false,
      [FIELD_NAMES.CUSTOM_HOURS]: workingHours.customHours || {},
      [FIELD_NAMES.SINGLE_HOUR]: {
        startTime: null,
        endTime: null,
      },
      [FIELD_NAMES.IS_INSTRUCTOR]: isInstructor,
      defaultCheckedDays: [],
    }
  }

  // Destructure customHoursEnabled from workingHours

  const hasCustomHours = customHoursEnabled || false

  // Convert HH:mm strings to Date objects for each day
  const customHours = weekdaysTimeStartToEnd(dayHours)

  // Get list of days that have hours set (for defaultChecked prop)
  const daysWithHours = customHours
    ? Object.entries(customHours)
        .filter(([_, dayHours]) => dayHours && dayHours.startTime && dayHours.endTime)
        .map(([day]) => day.toLowerCase())
    : []

  // For single hour mode, use the first available day's hours or null
  const singleHourData =
    !hasCustomHours && customHours
      ? Object.values(customHours).find((day) => day?.startTime && day?.endTime)
      : null

  return {
    [FIELD_NAMES.FIRST_NAME]: userData.firstName,
    [FIELD_NAMES.LAST_NAME]: userData.lastName,
    [FIELD_NAMES.EMAIL]: userData.email,
    [FIELD_NAMES.EMPLOYEE_NUMBER]:
      userData.employeeNumber != null ? String(userData.employeeNumber) : '',
    [FIELD_NAMES.PHONE]: userData.phone,
    [FIELD_NAMES.ABOUT]: userData.about,
    [FIELD_NAMES.HOBBIES]: userData.hobbies,
    [FIELD_NAMES.INSTAGRAM]: userData.instagramHandle,
    [FIELD_NAMES.IMAGE]: userData?.profileImage,
    [FIELD_NAMES.USER_ACCESS_RULES]: userData.rules.map((rule) => ({
      [FIELD_NAMES.RULE_ID]: rule.staffAccessRuleId,
      [FIELD_NAMES.POLICY]: {
        id: rule.policy?.staffAccessPolicyId,
        name: rule.policy?.name,
        type: rule.assignmentType,
      },
      [FIELD_NAMES.SITES]: {
        list:
          rule.sites?.map((site) => ({
            id: site.siteId,
            name: site.name,
          })) ?? [],
        siteIds: rule.sites?.map((site) => site.siteId) ?? [],
      },
      [FIELD_NAMES.SITE_GROUPS]: {
        list:
          rule.siteGroups?.map((group) => ({
            id: group.siteGroupId,
            name: group.name,
          })) ?? [],
        siteGroupIds: rule.siteGroups?.map((group) => group.siteGroupId) ?? [],
      },
    })),
    [FIELD_NAMES.ENABLE_WORKING_HOURS]:
      !!workingHours &&
      Object.keys(dayHours).some((day) => dayHours[day]?.startTime && dayHours[day]?.endTime),
    [FIELD_NAMES.HAS_CUSTOM_HOURS]: hasCustomHours,
    [FIELD_NAMES.CUSTOM_HOURS]: hasCustomHours ? customHours : {},
    [FIELD_NAMES.SINGLE_HOUR]: singleHourData ?? {
      startTime: null,
      endTime: null,
    },
    [FIELD_NAMES.IS_INSTRUCTOR]: isInstructor,
    defaultCheckedDays: daysWithHours,
  }
}

export const transformFormValuesToTableRow = (formValues, id) => {
  return {
    id,
    profile: {
      firstName: formValues[FIELD_NAMES.FIRST_NAME],
      lastName: formValues[FIELD_NAMES.LAST_NAME],
      email: formValues[FIELD_NAMES.EMAIL],
      phone: formValues[FIELD_NAMES.PHONE],
      about: formValues[FIELD_NAMES.ABOUT],
      hobbies: formValues[FIELD_NAMES.HOBBIES],
      employeeNumber: formValues[FIELD_NAMES.EMPLOYEE_NUMBER],
      instagramHandle: formValues[FIELD_NAMES.INSTAGRAM],
      profileImage: formValues[FIELD_NAMES.IMAGE],
    },
    access: {
      rules: formValues[FIELD_NAMES.USER_ACCESS_RULES].map((rule) => ({
        id: rule[FIELD_NAMES.RULE_ID],
        accessPolicy: {
          id: rule[FIELD_NAMES.POLICY].id,
          name: rule[FIELD_NAMES.POLICY].name,
          type: rule[FIELD_NAMES.POLICY].type,
        },
        sites: rule[FIELD_NAMES.SITES].list.map((site) => ({
          id: site.id,
          name: site.name,
        })),
        siteGroups: rule[FIELD_NAMES.SITE_GROUPS].list.map((group) => ({
          id: group.id,
          name: group.name,
        })),
      })),
    },
  }
}

export const transformTableAccessFilters = (filter) => {
  if (!filter || Object.keys(filter).length === 0) {
    return { status: USERS_STATUSES_OPTIONS.active.key }
  }

  const processedFilter = { ...filter }

  if (processedFilter.status) {
    if (processedFilter.status.value === USERS_STATUSES_OPTIONS.allUsers.key) {
      delete processedFilter.status
    }
  } else {
    processedFilter.status = USERS_STATUSES_OPTIONS.active.key
  }

  return _.mapValues(processedFilter, (valueObj) => {
    if (valueObj && typeof valueObj === 'object' && 'value' in valueObj) {
      const value = valueObj.value

      if (value === ACCESS_OPTIONS.noAccess.key) {
        return undefined
      }

      if (Array.isArray(value)) {
        return value.length > 0 ? value.join(',') : undefined
      }

      return value || undefined
    }

    if (Array.isArray(valueObj)) {
      return valueObj.length > 0 ? valueObj.join(',') : undefined
    }
    return valueObj || undefined
  })
}
